import {
    CalendarToday as CalendarIcon,
    ChangeCircleOutlined,
    ChevronRight as IconChevronRight,
    People as IconPeople,
} from "@mui/icons-material";
import { darken, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";
import { VisitStatusTimeline } from "~/shared/components/VisitStatusTimeline";

import { IconVisit } from "../IconVisit";
import { VisitListItemFragment } from "./fragments.generated";

type VisitListItemProps = {
    href: To;
    visit: VisitListItemFragment;
    selectedDate: Date;
};

export function VisitListItem({ visit, href, selectedDate }: VisitListItemProps) {
    const { t } = useTranslation("planner");

    return (
        <Stack
            paddingInline={2}
            paddingBlock={2}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            component={Link}
            to={href}
            sx={theme => ({
                color: theme.palette.text.primary,
                textDecoration: "none",
                "&:hover": {
                    backgroundColor: darken(theme.palette.background.paper, 0.03),
                },
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
            <Stack padding={1} flexGrow={1} rowGap={1}>
                <Stack direction="row" columnGap={2}>
                    <Stack justifyContent="center" alignItems="center" gap={1}>
                        <IconVisit status={visit.plannerStatus} />
                        {visit.hasStagedChanges && <ChangeCircleOutlined color="warning" fontSize="small" />}
                    </Stack>

                    <Stack flexGrow={1}>
                        <Typography flexGrow={1} variant="body1">
                            {visit.protocol.name}
                        </Typography>
                        <Typography flexGrow={1} variant="subtitle2" color="textSecondary">
                            {visit.protocolVisit.name ?? t("visitInfo.visitNumber", { visitNumber: visit.protocolVisit.number })}
                        </Typography>
                        <Typography
                            color="textSecondary"
                            variant="caption"
                            sx={{ display: "flex", alignItems: "center", columnGap: 1 }}
                        >
                            <IconPeople fontSize="inherit" /> This visit has {visit.plannedFieldworkersStaged}/
                            {visit.requiredFieldworkers} fieldworkers
                        </Typography>
                        {visit.startTime && (
                            <Typography
                                color="textSecondary"
                                variant="caption"
                                sx={{ display: "flex", alignItems: "center", columnGap: 1 }}
                            >
                                <CalendarIcon fontSize="inherit" /> {formatDateTime(parseStringToDate(visit.startTime), "long")}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
                <VisitStatusTimeline
                    possibleDays={visit.possibleDays}
                    selectedDate={selectedDate}
                    plannerStatus={visit.plannerStatus}
                    variant="minimal"
                />
            </Stack>
            <IconChevronRight />
        </Stack>
    );
}
